import { useAuthStore } from "@/stores/auth/auth-store";
import { BaseApi } from "./base-api";
import { io } from "socket.io-client";
import { installAuthRefreshInterceptor } from "./auth-refresh";

const baseURL =
  import.meta.env.VITE_API_URL || "https://api.articulate.localhost";

const getAuthorizationHeader = () => {
  const token = useAuthStore.getState().authToken;
  return token ? `Bearer ${token}` : "";
};

export const API = new BaseApi({
  baseURL,
  headers: {
    Authorization: getAuthorizationHeader(),
  },
});

installAuthRefreshInterceptor(API.instance, {
  getAccessToken: () => useAuthStore.getState().authToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  refresh: async (refreshToken) => {
    const { data } = await API.auth.authControllerRefresh({ refreshToken });
    return data;
  },
  onRefreshSucceeded: ({ accessToken, refreshToken }) => {
    useAuthStore.getState().setTokens(accessToken, refreshToken);
    updateAuthorizationHeader(accessToken);
  },
  onRefreshDenied: () => {
    useAuthStore.getState().setTokens(null, null);
    removeAuthHeader();
    window.location.href = "/login";
  },
});

export const socket: ReturnType<typeof io> = io(baseURL, {
  transports: ["websocket", "polling"],
  autoConnect: false,
  forceNew: true,
  auth: (cb) => cb({ token: useAuthStore.getState().authToken }),
});

// Reference-counted rather than a Set: multiple hooks/components can each
// want the same chatRoomId joined at once (e.g. a chat detail view and a
// chat list mounted together). Leaving must only emit "leaveRoom" once the
// last interested caller has left, or an unrelated caller's unmount would
// silently drop everyone else's subscription until the next reconnect.
const activeChatRoomRefCounts = new Map<string, number>();

/**
 * Room membership on the socket server is per-connection, so every
 * (re)connect - token refresh, network drop, tab wake-up - drops it.
 * Re-emitting joinRoom here keeps rooms the app cares about subscribed
 * without every call site having to know about reconnects.
 */
socket.on("connect", () => {
  activeChatRoomRefCounts.forEach((_, chatRoomId) => {
    socket.emit("joinRoom", { chatRoomId });
  });
});

export const joinChatRoom = (chatRoomId: string) => {
  const nextCount = (activeChatRoomRefCounts.get(chatRoomId) ?? 0) + 1;
  activeChatRoomRefCounts.set(chatRoomId, nextCount);
  if (nextCount === 1) {
    socket.emit("joinRoom", { chatRoomId });
  }
};

export const leaveChatRoom = (chatRoomId: string) => {
  const currentCount = activeChatRoomRefCounts.get(chatRoomId) ?? 0;
  if (currentCount <= 1) {
    activeChatRoomRefCounts.delete(chatRoomId);
    socket.emit("leaveRoom", { chatRoomId });
  } else {
    activeChatRoomRefCounts.set(chatRoomId, currentCount - 1);
  }
};

if (useAuthStore.getState().authToken) {
  socket.connect();
}

useAuthStore.subscribe((state, prevState) => {
  if (state.authToken && state.authToken !== prevState.authToken) {
    socket.disconnect();
    socket.connect();
  } else if (!state.authToken && prevState.authToken) {
    socket.disconnect();
  }
});

export const updateAuthorizationHeader = (token: string) => {
  API.instance.defaults.headers.Authorization = `Bearer ${token}`;
};

export const removeAuthHeader = () => {
  API.instance.defaults.headers.Authorization = "";
};
